import { useState } from "react";
import type { AdminPaymentListItemResponse } from "../../../types/adminTypes";
import { formatDateTime } from "../../../utils/DateUtils";
import { formatUsdFromCents } from "../../../utils/CurrencyUtils";
import Button from "../../ui/Button/Button";
import AdminTableShell from "../ui/AdminTableShell";
import AdminPillBadge from "../ui/AdminPillBadge";
import AdminPaymentDetailModal from "./AdminPaymentDetailModal";
import AdminPaymentsFilters from "./AdminPaymentsFilters";
import AdminPaymentsTableRow from "./AdminPaymentsTableRow";
import { adminPaymentsColumns } from "./adminPaymentsColumns";

type AdminPaymentsTableProps = {
  payments: AdminPaymentListItemResponse[];
  isLoading: boolean;
  canWrite: boolean;
  search: string;
  paymentType: "GIG" | "BUSINESS_LISTING_FEE";
  status: string;
  createdFromDate: string;
  createdToDate: string;
  pageSize: number;
  page: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onPaymentTypeChange: (value: "GIG" | "BUSINESS_LISTING_FEE") => void;
  onStatusChange: (value: string) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onPageChange: (nextPage: number) => void;
};

const AdminPaymentsTable = ({
  payments,
  isLoading,
  canWrite,
  search,
  paymentType,
  status,
  createdFromDate,
  createdToDate,
  pageSize,
  page,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onPaymentTypeChange,
  onStatusChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onPageSizeChange,
  onPageChange,
}: AdminPaymentsTableProps) => {
  const [selectedPayment, setSelectedPayment] = useState<AdminPaymentListItemResponse | null>(null);
  const statusTone: Record<string, "neutral" | "success" | "warning" | "danger"> = {
    CHECKOUT_PENDING: "warning",
    FUNDED: "warning",
    RELEASE_READY: "warning",
    PAID_OUT: "success",
    PAID: "success",
    REFUNDED: "danger",
    PARTIALLY_REFUNDED: "danger",
    PARTIALLY_SETTLED: "success",
    TRANSFER_REVERSED: "danger",
    FAILED: "danger",
  };

  return (
    <>
      <AdminTableShell
        columns={adminPaymentsColumns}
        filters={
          <AdminPaymentsFilters
            search={search}
            paymentType={paymentType}
            status={status}
            createdFromDate={createdFromDate}
            createdToDate={createdToDate}
            pageSize={pageSize}
            onSearchChange={onSearchChange}
            onPaymentTypeChange={onPaymentTypeChange}
            onStatusChange={onStatusChange}
            onCreatedFromDateChange={onCreatedFromDateChange}
            onCreatedToDateChange={onCreatedToDateChange}
            onPageSizeChange={onPageSizeChange}
          />
        }
        mobileCards={payments.map((payment) => (
          <article key={payment.id} className="rounded-[1.5rem] border border-myteengig-border bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-myteengig-text-primary">{payment.gigTitle ?? "Untitled payment"}</p>
                <p className="mt-1 text-sm text-myteengig-text-muted">{payment.payoutOwnerLabel}</p>
              </div>
              <AdminPillBadge tone={statusTone[payment.status] ?? "neutral"}>{payment.status}</AdminPillBadge>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Participants</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{payment.paymentType === "GIG" ? `Teen: ${payment.teenName}` : "Business listing fee"}</p>
                <p className="mt-1 text-sm text-myteengig-text-muted">Adult: {payment.adultName}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Charge</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">
                  {payment.totalChargeCents !== null ? formatUsdFromCents(payment.totalChargeCents) : "Not charged"}
                </p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Created</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{formatDateTime(payment.createdAt)}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Issue status</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{payment.latestIssueStatus ?? "No issue"}</p>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setSelectedPayment(payment)}>
                View
              </Button>
            </div>
          </article>
        ))}
        rows={payments.map((payment) => (
          <AdminPaymentsTableRow key={payment.id} payment={payment} onView={setSelectedPayment} />
        ))}
        isLoading={isLoading}
        isEmpty={!isLoading && payments.length === 0}
        emptyMessage="No payments found for the current filters."
        page={page}
        pageSize={pageSize}
        resultsCount={resultsCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={onPageChange}
      />
      <AdminPaymentDetailModal
        payment={selectedPayment}
        isOpen={selectedPayment !== null}
        canWrite={canWrite}
        onClose={() => setSelectedPayment(null)}
      />
    </>
  );
};

export default AdminPaymentsTable;
